import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  BusinessCenterOutlined,
  ForumOutlined,
  Refresh,
  SchoolOutlined,
  SecurityOutlined,
} from '@material-ui/icons';

import UsersAPI from '../../user-api';
import '../../../../assets/styles/content/mentor-professional-experience.scss';

const EMPTY_VALUE = 'Sin información registrada';

const hasValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const formatValue = (value) => {
  if (!hasValue(value)) {
    return EMPTY_VALUE;
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
};

const formatBoolean = (value) => {
  if (value === true) {
    return 'Sí';
  }

  if (value === false) {
    return 'No';
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (['si', 'sí', 'true', '1', 'yes'].includes(normalizedValue)) {
      return 'Sí';
    }

    if (['no', 'false', '0'].includes(normalizedValue)) {
      return 'No';
    }
  }

  return EMPTY_VALUE;
};

const CURRENT_FIELDS = {
  background: [
    { key: 'job', label: 'Profesión u oficio' },
    {
      key: 'mentorExp',
      label: '¿En qué año realizó su primera mentoría?',
    },
  ],
  motivation: [
    {
      key: 'discoverySource',
      label: '¿Cómo conoció ChileConverge?',
    },
    {
      key: 'mentoringTopics',
      label:
        'Temas generales y específicos en los que puede aportar en sus mentorías',
      multiline: true,
      wide: true,
    },
    {
      key: 'helping',
      label: 'Reseña personal y profesional',
      multiline: true,
      wide: true,
    },
  ],
  preferences: [
    {
      key: 'whatsappOptIn',
      label: 'Participación en el grupo de WhatsApp de mentores',
      type: 'boolean',
    },
    {
      key: 'shareContactOptIn',
      label: 'Autorización para compartir su información de contacto',
      type: 'boolean',
    },
  ],
};

const CURRENT_EXPERIENCE_FIELDS = [
  ...CURRENT_FIELDS.background,
  ...CURRENT_FIELDS.motivation,
  ...CURRENT_FIELDS.preferences,
];

const Section = ({ fields, icon, title, values }) => (
  <article className="mentor-professional-experience__card">
    <header className="mentor-professional-experience__card-header">
      <span className="mentor-professional-experience__card-icon" aria-hidden="true">
        {icon}
      </span>
      <h4>{title}</h4>
    </header>
    <div className="mentor-professional-experience__fields">
      {fields.map((field) => {
        const displayValue =
          field.type === 'boolean'
            ? formatBoolean(values[field.key])
            : formatValue(values[field.key]);

        return (
          <div
            className={`mentor-professional-experience__field${
              field.wide ? ' mentor-professional-experience__field--wide' : ''
            }`}
            key={field.key}
          >
            <span className="mentor-professional-experience__label">
              {field.label}
            </span>
            <p
              className={`mentor-professional-experience__value${
                field.multiline
                  ? ' mentor-professional-experience__value--multiline'
                  : ''
              }${
                displayValue === EMPTY_VALUE
                  ? ' mentor-professional-experience__value--empty'
                  : ''
              }`}
            >
              {displayValue}
            </p>
          </div>
        );
      })}
    </div>
  </article>
);

Section.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      multiline: PropTypes.bool,
      type: PropTypes.string,
      wide: PropTypes.bool,
    })
  ).isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
};

const ProfessionalExperience = ({ userId }) => {
  const requestIdRef = useRef(0);
  const [experience, setExperience] = useState(null);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadExperience = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');

    try {
      const response = await UsersAPI.getMentorProfessionalExperience(userId);

      if (requestId !== requestIdRef.current) {
        return;
      }

      const responseData = response?.data || {};
      setExperience(
        responseData.experience && typeof responseData.experience === 'object'
          ? responseData.experience
          : null
      );
      setPillars(Array.isArray(responseData.pillars) ? responseData.pillars : []);
    } catch (requestError) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setExperience(null);
      setPillars([]);
      setError(
        requestError?.data?.message ||
          'No fue posible cargar la experiencia profesional del mentor.'
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    loadExperience();

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadExperience]);

  const values = experience || {};
  const hasInformation =
    pillars.length > 0 ||
    CURRENT_EXPERIENCE_FIELDS.some((field) => hasValue(values[field.key]));

  return (
    <section
      className="mentor-professional-experience"
      aria-labelledby="mentor-professional-experience-title"
    >
      <header className="mentor-professional-experience__hero">
        <span className="mentor-professional-experience__hero-icon" aria-hidden="true">
          <BusinessCenterOutlined />
        </span>
        <div>
          <span className="mentor-professional-experience__eyebrow">
            Perfil del mentor
          </span>
          <h3 id="mentor-professional-experience-title">
            Experiencia profesional
          </h3>
          <p>
            Antecedentes, áreas de conocimiento y preferencias registradas por
            el mentor.
          </p>
        </div>
      </header>

      {loading ? (
        <div
          className="mentor-professional-experience__loading"
          aria-busy="true"
          aria-label="Cargando experiencia profesional"
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              className="mentor-professional-experience__skeleton"
              key={`professional-experience-skeleton-${index}`}
              variant="rect"
            />
          ))}
        </div>
      ) : error ? (
        <Alert
          action={
            <button
              className="mentor-professional-experience__retry"
              onClick={loadExperience}
              type="button"
            >
              <Refresh fontSize="small" />
              Reintentar
            </button>
          }
          className="mentor-professional-experience__error"
          severity="error"
        >
          {error}
        </Alert>
      ) : (
        <>
          {!hasInformation && (
            <div
              className="mentor-professional-experience__empty"
              role="status"
            >
              Este mentor aún no registra información de experiencia profesional.
            </div>
          )}

          <div className="mentor-professional-experience__sections">
            <Section
              fields={CURRENT_FIELDS.background}
              icon={<BusinessCenterOutlined />}
              title="Antecedentes y experiencia"
              values={values}
            />
            <Section
              fields={CURRENT_FIELDS.motivation}
              icon={<ForumOutlined />}
              title="Motivación del mentor"
              values={values}
            />
            <article className="mentor-professional-experience__card">
              <header className="mentor-professional-experience__card-header">
                <span
                  className="mentor-professional-experience__card-icon"
                  aria-hidden="true"
                >
                  <SchoolOutlined />
                </span>
                <h4>Áreas de conocimiento</h4>
              </header>
              <div className="mentor-professional-experience__fields">
                <div className="mentor-professional-experience__field mentor-professional-experience__field--wide">
                  <span className="mentor-professional-experience__label">
                    Pilares seleccionados
                  </span>
                  {pillars.length ? (
                    <div className="mentor-professional-experience__pillars">
                      {pillars.map((pillar) => (
                        <Chip
                          className="mentor-professional-experience__pillar"
                          key={pillar.id || pillar.name}
                          label={pillar.name || EMPTY_VALUE}
                          title={pillar.description || pillar.name || ''}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="mentor-professional-experience__value mentor-professional-experience__value--empty">
                      {EMPTY_VALUE}
                    </p>
                  )}
                </div>
              </div>
            </article>
            <Section
              fields={CURRENT_FIELDS.preferences}
              icon={<SecurityOutlined />}
              title="Preferencias y autorizaciones"
              values={values}
            />
          </div>
        </>
      )}
    </section>
  );
};

ProfessionalExperience.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default ProfessionalExperience;
